import type { Option } from "@tento-lona/sheets";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import type { SheetHost } from "../client/sheet-host.ts";
import { type Actor, type InvalidationSink, LayoutChanged } from "../invalidation.ts";
import { StoredPreference } from "./stored-preference.ts";

/** The sidebar's width when expanded and when collapsed, in pixels. */
export type SidebarDimensions = {
  expandedWidth: number;
  unexpandedWidth: number;
};

const STORAGE_KEY = "sidebar-width";
const MIN_WIDTH = 300;
const DEFAULT_EXPANDED = 360;
const DEFAULT_UNEXPANDED = 300;

/**
 * The sidebar's stored widths, expanded and collapsed
 * (`client.preferences.sidebarWidth()`): `peek()` reads them,
 * `set(dimensions, actor)` stores them.
 */
export class SidebarWidthPreferences {
  /** The widths when none are stored: `peek() ?? SidebarWidthPreferences.DEFAULT`. */
  static readonly DEFAULT: SidebarDimensions = Object.freeze({
    expandedWidth: DEFAULT_EXPANDED,
    unexpandedWidth: DEFAULT_UNEXPANDED,
  });
  readonly #storage: PreferencesStorage;
  readonly #host: SheetHost;
  readonly #invalidation: InvalidationSink;
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, host: SheetHost, invalidation: InvalidationSink, lifecycle: ClientLifecycle) {
    this.#storage = storage;
    this.#host = host;
    this.#invalidation = invalidation;
    this.#lifecycle = lifecycle;
  }

  /** Stored widths, clamped; `null` when absent or unusable (logged). */
  peek(): Option<SidebarDimensions> {
    const parsed = StoredPreference.record(
      STORAGE_KEY,
      StoredPreference.json(this.#storage, STORAGE_KEY),
    );
    if (parsed == null) return null;
    const expanded = StoredPreference.finite(STORAGE_KEY, parsed["expandedWidth"] ?? null);
    const unexpanded = StoredPreference.finite(STORAGE_KEY, parsed["unexpandedWidth"] ?? null);
    if (expanded == null || unexpanded == null) return null;
    const maxWidth = this.#host.getMaxWidth?.() ?? 1820;
    return {
      expandedWidth: Math.min(Math.max(expanded, MIN_WIDTH), maxWidth),
      unexpandedWidth: Math.min(Math.max(unexpanded, MIN_WIDTH), maxWidth),
    };
  }

  /** Store the widths and announce `LayoutChanged` stamped with `actor`, the writer. */
  set(dimensions: SidebarDimensions, actor: Actor): void {
    this.#lifecycle.assertLive("preferences.sidebarWidth.set");
    const { expandedWidth, unexpandedWidth } = dimensions;
    this.#storage.setItem(STORAGE_KEY, JSON.stringify({ expandedWidth, unexpandedWidth }));
    this.#invalidation.emit(new LayoutChanged(actor));
  }
}
